import type { ReactNode } from 'react';

interface PillNavigationItem {
  label: string;
  mobileLabel?: string;
  value: string;
  icon?: ReactNode;
}

interface PillNavigationProps {
  items: PillNavigationItem[];
  activeItem: string;
  onChange: (value: string) => void;
  className?: string;
}

/* Name kept for its call site on HomePage, but the shape is no longer a pill:
   the brand is 4–5px corners everywhere and pills are out. The active tab is a
   brand-blue fill on a sunken track — the same "current" treatment the rest of
   the system uses. */
export function PillNavigation({
  items,
  activeItem,
  onChange,
  className = '',
}: PillNavigationProps) {
  return (
    <nav
      className={`flex bg-surface-sunken rounded-md p-1 ${className}`}
      role="tablist"
      aria-label="Category navigation"
    >
      {items.map((item) => (
        <button
          key={item.value}
          type="button"
          role="tab"
          aria-selected={activeItem === item.value}
          aria-controls={`panel-${item.value}`}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-md
            transition-all duration-300 ease-in-out
            font-medium text-sm md:text-base
            ${
              activeItem === item.value
                ? 'bg-primary text-white shadow-md'
                : 'text-muted hover:bg-surface-raised hover:text-foreground'
            }
          `}
          onClick={() => onChange(item.value)}
        >
          {item.icon && <span className="w-5 h-5">{item.icon}</span>}
          {item.mobileLabel ? (
            <>
              <span className="md:hidden">{item.mobileLabel}</span>
              <span className="hidden md:inline">{item.label}</span>
            </>
          ) : item.label}
        </button>
      ))}
    </nav>
  );
}
